import { useState } from 'react';
import { PerplexityContent } from '@/components/perplexity-text-content/types/PerplexityContent';
import { generatePDFFromHTML, markdownToHTML, downloadPDF } from '@/utils/pdf-creator';
import { toast } from '@/hooks/errors/use-toast';

export const useResearchPDF = () => {
  const [isGenerating, setIsGenerating] = useState(false);

  const generatePDF = async (content: PerplexityContent) => {
    setIsGenerating(true);
    
    try {
      // Generate the same markdown content as the current download functionality
      const markdownContent = `# ${content.title}

**Research Type:** ${content.metadata?.researchType || content.contentType}

**Created:** ${content.metadata?.createdAt || 'Unknown'}

---

${content.content}`;

      // Convert markdown to HTML with enhanced styling
      const htmlContent = markdownToHTML(markdownContent);
      
      // Generate PDF filename
      const filename = `${content.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`;
      
      // Generate PDF
      const result = await generatePDFFromHTML(htmlContent, {
        filename,
        format: 'a4',
        orientation: 'portrait',
        margin: { top: 20, right: 20, bottom: 20, left: 20 },
        quality: 0.95
      });

      if (result.success && result.blob && result.filename) {
        downloadPDF(result.blob, result.filename);
        
        toast({
          title: "PDF Generated Successfully",
          description: "Your research has been downloaded as a PDF file",
        });
      } else {
        throw new Error(result.error || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('PDF generation failed:', error);
      
      toast({
        title: "PDF Generation Failed",
        description: error instanceof Error ? error.message : "Failed to generate PDF. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return { generatePDF, isGenerating };
}; 